/**
 * Popup for individual POTA parks (not necessarily overlap candidates).
 */

import { toLonLat } from 'ol/proj.js';

import { formatCoords, googleMapsUrl } from './candidate-popup.js';

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function wikiTitleFromParkName(title) {
    return String(title || '').trim().replace(/ /g, '_');
}

/**
 * @param {import('ol/Feature').default} feature
 */
function featureLatLon(feature) {
    const geom = feature.getGeometry();
    if (!geom) {
        return null;
    }

    const type = geom.getType();
    if (type === 'Point') {
        const [x, y] = geom.getCoordinates();
        const [lon, lat] = toLonLat([x, y]);
        return { lat, lon };
    }

    const extent = geom.getExtent();
    const centerX = (extent[0] + extent[2]) / 2;
    const centerY = (extent[1] + extent[3]) / 2;
    const [lon, lat] = toLonLat([centerX, centerY]);
    return { lat, lon };
}

/**
 * Normalize park properties from POTA points, activation geometries, or boundaries.
 *
 * @param {import('ol/Feature').default} feature
 */
export function parkFromFeature(feature) {
    const props = feature.getProperties();
    const ref = props.reference || props.NAME || props.name || null;
    const title = props.title || props.TITLE || props.sourceName || props.name || '';
    const latLon = featureLatLon(feature);

    return {
        ref,
        title,
        grid: props.GRID || null,
        refType: props.refType || props.PARK_TYPE || null,
        matchMethod: props.matchMethod || null,
        matchConfidence: props.matchConfidence || null,
        matchSource: props.matchSource || null,
        activationGeometryType: props.activationGeometryType || null,
        lat: latLon?.lat ?? null,
        lon: latLon?.lon ?? null,
    };
}

/**
 * @param {ReturnType<typeof parkFromFeature>} park
 */
export function buildParkPopupHtml(park) {
    const refLine = park.ref
        ? `<div><strong>${escapeHtml(park.ref)}</strong> — ${escapeHtml(park.title)}</div>`
        : `<div><strong>${escapeHtml(park.title)}</strong></div>`;

    const meta = [];
    if (park.grid) {
        meta.push(`Grid: ${escapeHtml(park.grid)}`);
    }
    if (park.refType) {
        meta.push(`Type: ${escapeHtml(park.refType)}`);
    }
    if (park.matchMethod) {
        meta.push(`Match: ${escapeHtml(park.matchMethod)} (${escapeHtml(park.matchConfidence || '—')})`);
    }
    if (park.activationGeometryType) {
        meta.push(`Geometry: ${escapeHtml(park.activationGeometryType)}`);
    }

    const coords = park.lat != null && park.lon != null
        ? formatCoords(park.lat, park.lon)
        : '—';

    const potaUrl = park.ref ? `https://pota.app/#park/${encodeURIComponent(park.ref)}` : null;
    const wikiUrl = park.title
        ? `https://pota.miraheze.org/wiki/${encodeURIComponent(wikiTitleFromParkName(park.title))}`
        : null;

    return `
        <div class="pa-park-popup">
            <div class="pa-popup-drag-handle d-flex justify-content-between align-items-start gap-2 mb-2" title="Drag to move">
                <div class="pa-popup-refs">${refLine}</div>
                <button type="button" class="btn-close btn-close-sm" data-action="close" aria-label="Close"></button>
            </div>
            ${meta.length ? `<div class="small text-muted mb-2">${meta.join(' · ')}</div>` : ''}
            <div class="mb-2">
                <div class="small text-muted">Reference point</div>
                <code class="pa-coord-string">${escapeHtml(coords)}</code>
            </div>
            <div class="d-flex flex-wrap gap-2 mb-1">
                ${potaUrl ? `<a class="btn btn-sm btn-outline-primary" href="${potaUrl}" target="_blank" rel="noopener">POTA.app</a>` : ''}
                ${wikiUrl ? `<a class="btn btn-sm btn-outline-secondary" href="${wikiUrl}" target="_blank" rel="noopener">Wiki</a>` : ''}
                ${park.lat != null ? `<a class="btn btn-sm btn-outline-success" href="${googleMapsUrl(park.lat, park.lon)}" target="_blank" rel="noopener">Google Maps</a>` : ''}
            </div>
        </div>
    `;
}

/**
 * @param {HTMLElement} container
 * @param {ReturnType<typeof parkFromFeature>} park
 * @param {{ onClose?: () => void }} handlers
 */
export function mountParkPopup(container, park, handlers = {}) {
    container.innerHTML = buildParkPopupHtml(park);
    container.querySelector('[data-action="close"]')?.addEventListener('click', () => {
        handlers.onClose?.();
    });
}
